import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Calendar, Mail, Phone, Globe, Bell, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { COUNTRY_NAMES } from '../utils/currency';
import NotificationsPanel from '../components/shared/NotificationsPanel';

// Gender options
const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// Helper to convert country name to code if needed
const getCountryCode = (value) => {
  if (!value) return '';
  // If it's already a valid country code, return it
  if (COUNTRY_NAMES[value]) return value;
  // Try to find the code by country name (case-insensitive)
  // Normalize by trimming and comparing lowercase
  const normalizedValue = value.trim().toLowerCase();
  const entry = Object.entries(COUNTRY_NAMES).find(
    ([, name]) => name.toLowerCase() === normalizedValue
  );
  return entry ? entry[0] : value;
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, user, completeProfile, updateProfile, profileComplete, isLoading, fetchUserData } = useAuth();
  const { isDesktop } = useResponsive();
  const fromSignup = location.state?.fromSignup;
  const fromVerification = location.state?.fromVerification;
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Use API user data first, then fall back to local userProfile form data
  const [formData, setFormData] = useState({
    firstName: user?.first_name || user?.firstName || userProfile.firstName || '',
    lastName: user?.last_name || user?.lastName || userProfile.lastName || '',
    email: user?.email || userProfile.email || '',
    phone: user?.phone || user?.phone_number || user?.mobile || user?.identifier || userProfile.phone || '',
    dateOfBirth: user?.date_of_birth || user?.dateOfBirth || userProfile.dateOfBirth || '',
    gender: user?.gender || userProfile.gender || '',
    country: user?.country || userProfile.country || '',
  });

  // Update form when user data loads from API
  useEffect(() => {
    if (user) {
      console.log('ProfilePage - user object:', user);
      console.log('ProfilePage - user.gender:', user.gender);
      console.log('ProfilePage - user.country:', user.country);
      console.log('ProfilePage - user.phone:', user.phone);
      console.log('ProfilePage - user.phone_number:', user.phone_number);
      setFormData({
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        email: user.email || '',
        phone: user.phone || user.phone_number || user.mobile || user.identifier || '',
        dateOfBirth: user.date_of_birth || user.dateOfBirth || '',
        gender: user.gender || '',
        country: getCountryCode(user.country) || '',
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[ProfilePage] handleSubmit called with formData:', formData);
    setIsSaving(true);
    setSaveError('');

    try {
      // Call API to update profile
      console.log('[ProfilePage] Calling updateProfile...');
      const result = await updateProfile(formData);
      console.log('[ProfilePage] updateProfile result:', result);

      // Refresh user data to ensure AuthContext has latest from API
      // This ensures HomeTab shows updated greeting and hides "Complete Profile" banner
      await fetchUserData();
      console.log('[ProfilePage] User data refreshed');

      // Also update local state
      completeProfile(formData);

      // Navigate to home
      navigate('/home');
    } catch (error) {
      console.error('[ProfilePage] Failed to save profile:', error);
      setSaveError(error.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (fromSignup) {
      navigate('/home');
    } else {
      navigate(-1);
    }
  };

  const isValid =
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    (formData.email.trim() || formData.phone.trim()) &&
    formData.dateOfBirth;

  // Debug: Log validation state
  console.log('[ProfilePage] Form validation:', {
    isValid,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: formData.phone,
    dateOfBirth: formData.dateOfBirth,
  });

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Global Header with Logo and Notification */}
        <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
          <button
            onClick={() => setShowNotifications(true)}
            className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <Bell size={22} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
            )}
          </button>
        </header>

        {/* Sub Header with Back Arrow */}
        <div className="bg-white px-4 py-3 flex items-center gap-3 border-b border-gray-100">
          <button
            onClick={handleBack}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">
            {profileComplete ? 'Edit Profile' : 'Complete Your Profile'}
          </h1>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 px-6 py-6 flex flex-col">
          {!profileComplete && fromVerification && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 mb-6">
              <p className="text-indigo-800 text-sm">
                Great! Your identity is verified. Now complete your profile details to finish setting up your account.
              </p>
            </div>
          )}
          {!profileComplete && !fromVerification && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6">
              <p className="text-emerald-800 text-sm">
                Complete your profile to unlock your rewards →
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="Enter your first name"
                  className="input pl-11"
                />
              </div>
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Enter your last name"
                  className="input pl-11"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className="input pl-11"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className="input pl-11"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="input pl-11"
                />
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="input pl-11 appearance-none"
                >
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <div className="relative">
                <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className="input pl-11 appearance-none"
                >
                  <option value="">Select country</option>
                  {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex-1" />

          {/* Error Message */}
          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mt-4">
              <p className="text-red-600 text-sm">{saveError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || isSaving}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-all mt-6 ${
              isValid && !isSaving
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : profileComplete ? (
              'Save Changes'
            ) : (
              'Save'
            )}
          </button>
        </form>
      </div>

      {/* Notifications Panel */}
      {showNotifications && (
        <NotificationsPanel
          onClose={() => setShowNotifications(false)}
          onUnreadCountChange={setUnreadCount}
        />
      )}
    </div>
  );
}
